import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getUsers, getSites, getAssets, getInvoices } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Input } from '../../components/ui/input';
import { Building2, Mail, Phone, MapPin, Boxes, Search, ArrowRight, Link2, Wallet, AlertCircle } from 'lucide-react';

// Status values that mean "invoice is out to the customer and awaiting
// payment" — i.e. should count towards outstanding. Draft invoices don't
// count because they haven't been sent yet. Paid / voided invoices never
// count.
const OUTSTANDING_STATUSES = new Set([
  'sent', 'authorised', 'submitted', 'overdue', 'partially-paid',
  'AUTHORISED', 'SUBMITTED',
]);

const isOutstanding = (inv) => {
  const s = (inv.status || '').toString();
  const xs = (inv.xeroStatus || '').toString();
  if (s === 'paid' || s === 'voided' || s === 'deleted') return false;
  if (xs === 'PAID' || xs === 'VOIDED' || xs === 'DELETED') return false;
  if (OUTSTANDING_STATUSES.has(s) || OUTSTANDING_STATUSES.has(xs)) return true;
  return false;
};

const isOverdue = (inv) => {
  if (!isOutstanding(inv)) return false;
  const d = inv.dueDate ? new Date(inv.dueDate) : null;
  if (!d || isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
};

const fmtGbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    Promise.all([getUsers(), getSites(), getAssets(), getInvoices()])
      .then(([users, s, a, inv]) => {
        // ALL customers (parents + sub-customers) — the enrichment step
        // below rolls sub-customer sites/assets/invoices up into the
        // parent so counts stay accurate.
        setCustomers(users.filter(u => u.role === 'customer'));
        setSites(s);
        setAssets(a);
        setInvoices(inv || []);
      })
      .finally(() => setLoading(false));
  }, []);

  // Sub-customer → parent map, so we can attribute sub-customer sites,
  // assets and invoices back to the parent card the admin actually
  // sees on this page.
  const parentByChild = useMemo(() => {
    const map = {};
    customers.forEach(c => {
      if (c.parentCustomerId) map[c.id] = c.parentCustomerId;
    });
    return map;
  }, [customers]);
  const rollupId = (customerId) => parentByChild[customerId] || customerId;

  // Pre-bucket invoices by ROLLED-UP customer so each card render is O(1)
  const balancesByCustomer = useMemo(() => {
    const map = {};
    (invoices || []).forEach((inv) => {
      const cid = rollupId(inv.customerId);
      if (!cid) return;
      if (!map[cid]) map[cid] = { outstanding: 0, overdue: 0 };
      if (isOutstanding(inv)) map[cid].outstanding += Number(inv.amount || 0);
      if (isOverdue(inv)) map[cid].overdue += Number(inv.amount || 0);
    });
    return map;
  }, [invoices, parentByChild]);

  const enriched = useMemo(() => customers
    // Top-level customers only — sub-customers are edited from inside
    // the parent's detail page, so listing them at the top level
    // would just add noise.
    .filter(c => !c.parentCustomerId)
    .map(c => {
      const childIds = customers.filter(x => x.parentCustomerId === c.id).map(x => x.id);
      const scope = new Set([c.id, ...childIds]);
      return {
        ...c,
        subCustomerCount: childIds.length,
        siteCount: sites.filter(s => scope.has(s.customerId)).length,
        assetCount: assets.filter(a => scope.has(a.customerId)).length,
        outstanding: balancesByCustomer[c.id]?.outstanding || 0,
        overdue: balancesByCustomer[c.id]?.overdue || 0,
      };
    }), [customers, sites, assets, balancesByCustomer]);

  const filtered = enriched.filter(c => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (c.name || '').toLowerCase().includes(q)
        || (c.company || '').toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q);
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-customers-page">
      <PageHeader
        title="Customers"
        description={`${customers.length} customer${customers.length === 1 ? '' : 's'} on file. Click a customer to view their sites.`}
      />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid="customers-search"
          placeholder="Search by name, company or email..."
          className="pl-9"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      {loading && <div className="text-slate-500">Loading customers...</div>}

      {!loading && filtered.length === 0 && (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <Building2 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">No customers found</div>
          <div className="text-sm text-slate-500">Add customers via the Users page.</div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(c => (
            <Link
              key={c.id}
              to={`/admin/customers/${c.id}`}
              data-testid={`customer-card-${c.id}`}
              className="group bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm rounded-md p-5 transition-all"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="h-10 w-10 rounded bg-slate-900 text-white flex items-center justify-center font-bold text-sm shrink-0">
                  {c.avatar || (c.company || c.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate group-hover:text-blue-700 transition-colors">{c.company || c.name}</div>
                  {c.company && c.name && c.company !== c.name && (
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 truncate">
                      <Building2 className="h-3 w-3 shrink-0" /> {c.name}
                    </div>
                  )}
                  {c.subCustomerCount > 0 && (
                    <div
                      className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mt-1"
                      data-testid={`customer-subcount-${c.id}`}
                    >
                      +{c.subCustomerCount} sub-customer{c.subCustomerCount === 1 ? '' : 's'}
                    </div>
                  )}
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-900 transition-colors shrink-0" />
              </div>

              <div className="space-y-1.5 text-xs text-slate-600 pt-3 border-t border-slate-100">
                {c.email && (
                  <div className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 text-slate-400 shrink-0" /> {c.email}</div>
                )}
                {c.phone && (
                  <div className="flex items-center gap-1.5 truncate"><Phone className="h-3 w-3 text-slate-400 shrink-0" /> {c.phone}</div>
                )}
                {c.xeroContactId && (
                  <div className="flex items-center gap-1.5 text-emerald-700"><Link2 className="h-3 w-3 shrink-0" /> Linked to Xero</div>
                )}
              </div>

              <div className="grid grid-cols-4 gap-3 mt-4 pt-3 border-t border-slate-100">
                <div>
                  <div className="text-2xl font-display tracking-tight text-slate-900">{c.siteCount}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {c.siteCount === 1 ? 'site' : 'sites'}
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-display tracking-tight text-slate-900">{c.assetCount}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <Boxes className="h-3 w-3" /> {c.assetCount === 1 ? 'asset' : 'assets'}
                  </div>
                </div>
                <div data-testid={`customer-outstanding-${c.id}`}>
                  <div className={`text-2xl font-display tracking-tight ${c.outstanding > 0 ? 'text-slate-900' : 'text-slate-400'}`}>
                    {fmtGbp(c.outstanding)}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <Wallet className="h-3 w-3" /> outstanding
                  </div>
                </div>
                <div data-testid={`customer-overdue-${c.id}`}>
                  <div className={`text-2xl font-display tracking-tight ${c.overdue > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                    {fmtGbp(c.overdue)}
                  </div>
                  <div className={`text-[10px] uppercase tracking-wider flex items-center gap-1 ${c.overdue > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                    <AlertCircle className="h-3 w-3" /> overdue
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Customers;
